import moment from "moment";
import "./bill-summary-card.style.scss";
import { formatBsDateString, fromAdToBs } from "../../global/function";
import type { BilledItem } from "../invoice-template/invoice-template.component";
import { getSyncBadgeInfo } from "../bill-list/bill-list-item.component";
import type { Bill } from "../../queries/bill.queries";

// "ird-failed" isn't part of this anymore — it's now its own irdBillStatus
// prop below (split into "pending"/"failed" to match the two distinct badges
// bill-list-item.component.tsx's renderBillTags already shows), independent
// of the canceled/credit-issued document-lifecycle status.
export type SummaryStatusVariant = "canceled" | "credit-issued";

const STATUS_LABELS: Record<SummaryStatusVariant, string> = {
	canceled: "Canceled",
	"credit-issued": "Credit Note Issued",
};

interface BillSummaryCardProps {
	documentLabel: string;
	date?: string | null;
	// BE-stored Nepali form of `date` (e.g. Bill.invoice_date_nepali, dot-
	// separated "YYYY.MM.DD") — the value actually submitted to IRD, so it's
	// used instead of re-deriving via fromAdToBs to avoid drifting from it.
	dateNepali?: string | null;
	salesDate?: string | null;
	buyerName: string;
	buyerPan?: string | null;
	customerMobile?: string | null;
	paymentMode?: string | null;
	// Not available for drafts (Draft's company_user isn't populated by the
	// API) — omitted from the card whenever absent rather than guessed.
	recordedBy?: string | null;
	// Bill.print_copies / CreditNote.print_copies. Not tracked server-side for
	// drafts (no such column on Draft) — omitted there like recordedBy above.
	printCopies?: number;
	// Bill.lastPrintDate / CreditNote's equivalent — only meaningful alongside printCopies.
	lastPrintDate?: string | null;
	// CreditNote.ref_invoice_number / reason_for_return — only ever set when
	// documentLabel is a credit note; undefined the rest of the time.
	refInvoiceNumber?: string | null;
	reasonForReturn?: string | null;
	billedItems: BilledItem[];
	subTotal: number;
	totalSales: number;
	discount?: number;
	serviceCharge?: number;
	nonTaxableAmount?: number;
	// Bill.taxable_sales_vat (VAT mode) or Bill.amount_for_esf (ESF mode) —
	// whichever mode is active, mirroring invoice-template.component.tsx's
	// esfEnabled ? "ESF Applicable Amount" : "Taxable Amount" line.
	taxableAmount?: number;
	vat?: number;
	esf?: number;
	status?: SummaryStatusVariant;
	// Bill.ird_bill, mirrored as-is — "success" renders no badge (the common
	// case needs no callout), "pending"/"failed" each get their own badge,
	// same distinction bill-list-item.component.tsx's renderBillTags already
	// makes (not the coarser useBillStatus, which collapses both into one
	// "ird-failed" bucket for its own retry/print gating purposes).
	irdBillStatus?: Bill["ird_bill"];
	// Bill.bill_meta, mirrored as-is — present only when this bill is linked
	// to a connected external system. Renders the same synced/not-synced
	// badge as the list row, via the shared getSyncBadgeInfo helper.
	billMeta?: Bill["bill_meta"];
}

// Mirrors invoice-template.component.tsx's getBillDate: uses the BE-stored
// Nepali date (formatted via formatBsDateString) when the caller has one,
// otherwise derives it client-side via fromAdToBs.
function formatDatePair(
	enDate?: string | null,
	nepDateOverride?: string | null,
) {
	if (!enDate) return null;
	return {
		en: moment(enDate).format("DD MMMM, YYYY"),
		np: nepDateOverride
			? formatBsDateString(nepDateOverride)
			: fromAdToBs(enDate, "DD MMMM, YYYY"),
	};
}

// Lightweight, NOT pixel-exact replacement for rendering InvoiceTemplate on
// every bill/draft selection. Reads only already-computed, server-stored
// fields (sub_total/total_sales/discount/vat/esf etc.) and never recomputes
// anything client-side — the whole point is that this can't drift from the
// real PDF the way a second calculation engine could. The real, official
// document is only ever the BE-generated PDF, fetched on an explicit Print
// click (see PrintOptionsDialog / usePrintBill in bill.queries.ts).
export default function BillSummaryCard({
	documentLabel,
	date,
	dateNepali,
	salesDate,
	buyerName,
	buyerPan,
	customerMobile,
	paymentMode,
	recordedBy,
	printCopies,
	lastPrintDate,
	refInvoiceNumber,
	reasonForReturn,
	billedItems,
	subTotal,
	totalSales,
	discount,
	serviceCharge,
	nonTaxableAmount,
	taxableAmount,
	vat,
	esf,
	status,
	irdBillStatus,
	billMeta,
}: BillSummaryCardProps) {
	const invoiceDate = formatDatePair(date, dateNepali);
	const salesDatePair = formatDatePair(salesDate);
	// The vatable split only means anything once one of VAT/ESF mode is
	// actually active on this bill — mirrors invoice-template.component.tsx's
	// vatEnabled gate rather than always showing the column.
	const showVatableColumn = !!vat || !!esf;
	const syncBadge = billMeta ? getSyncBadgeInfo(billMeta) : null;

	return (
		<div className="bill-summary-card">
			<div className="bill-summary-card__header">
				<div>
					<h3>{documentLabel}</h3>
				</div>
				<div className="bill-summary-card__badges">
					{status && (
						<div
							className={`bill-summary-card__status bill-summary-card__status--${status}`}
						>
							{STATUS_LABELS[status]}
						</div>
					)}
					{irdBillStatus === "pending" && (
						<div className="bill-summary-card__status bill-summary-card__status--ird-pending">
							IRD Not Synced
						</div>
					)}
					{irdBillStatus === "failed" && (
						<div className="bill-summary-card__status bill-summary-card__status--ird-failed">
							IRD Failed
						</div>
					)}
					{syncBadge && (
						<div
							className={`bill-summary-card__status bill-summary-card__status--${syncBadge.synced ? "synced" : "sync-pending"}`}
						>
							{syncBadge.label}
						</div>
					)}
				</div>
			</div>

			<div className="bill-summary-card__buyer">
				<p className="bill-summary-card__buyer-name">
					Customer: <span>{buyerName}</span>
				</p>
				{buyerPan && <p>PAN/VAT: {buyerPan}</p>}
				{customerMobile && <p>{customerMobile}</p>}
			</div>

			{reasonForReturn && (
				<p className="bill-summary-card__reason">
					Reason for Return: <span>{reasonForReturn}</span>
				</p>
			)}

			<div className="bill-summary-card__meta">
				{refInvoiceNumber && (
					<div>
						<span>Ref. Invoice #</span>
						<span>{refInvoiceNumber}</span>
					</div>
				)}
				{invoiceDate && (
					<div>
						<span>Invoice Date</span>
						<span>
							{invoiceDate.np} <em>({invoiceDate.en})</em>
						</span>
					</div>
				)}
				{salesDatePair && (
					<div>
						<span>Sales Date</span>
						<span>
							{salesDatePair.np} <em>({salesDatePair.en})</em>
						</span>
					</div>
				)}
				{paymentMode && (
					<div>
						<span>Payment Method</span>
						<span>{paymentMode}</span>
					</div>
				)}
				{recordedBy && (
					<div>
						<span>Recorded By</span>
						<span>{recordedBy}</span>
					</div>
				)}
				{!!printCopies && (
					<div>
						<span>Printed</span>
						<span>
							{printCopies} time{printCopies > 1 ? "s" : ""}
							{lastPrintDate && (
								<em>
									{" "}
									(
									{moment(lastPrintDate).format(
										"DD MMM YYYY, hh:mm A",
									)}
									)
								</em>
							)}
						</span>
					</div>
				)}
			</div>

			<table className="bill-summary-card__items">
				<thead>
					<tr>
						<th>Item</th>
						<th>Qty</th>
						<th>Rate</th>
						<th>Amount</th>
						{showVatableColumn && <th>{esf ? "ESF" : "VAT"}</th>}
					</tr>
				</thead>
				<tbody>
					{billedItems.length === 0 ? (
						<tr>
							<td
								colSpan={showVatableColumn ? 5 : 4}
								className="bill-summary-card__no-items"
							>
								No items on this{" "}
								{documentLabel.split(" ")[0].toLowerCase()}
							</td>
						</tr>
					) : (
						billedItems.map((item, index) => (
							<tr key={index}>
								<td>{item.item_name}</td>
								<td>{item.item_qty}</td>
								<td>{item.item_price}</td>
								<td>{item.total_price?.toFixed(2)}</td>
								{showVatableColumn && (
									<td className="bill-summary-card__vatable-cell">
										{item.vatable ? "Yes" : "No"}
									</td>
								)}
							</tr>
						))
					)}
				</tbody>
			</table>

			<div className="bill-summary-card__totals">
				<div>
					<span>Sub Total</span>
					<span>Rs. {subTotal?.toFixed(2)}</span>
				</div>
				{!!discount && (
					<div>
						<span>Discount</span>
						<span>- Rs. {discount.toFixed(2)}</span>
					</div>
				)}
				{!!serviceCharge && (
					<div>
						<span>Service Charge</span>
						<span>Rs. {serviceCharge.toFixed(2)}</span>
					</div>
				)}
				{!!nonTaxableAmount && (
					<div>
						<span>Non-taxable Amount</span>
						<span>Rs. {nonTaxableAmount.toFixed(2)}</span>
					</div>
				)}
				{!!taxableAmount && (
					<div>
						<span>
							{esf ? "ESF Applicable Amount" : "Taxable Amount"}
						</span>
						<span>Rs. {taxableAmount.toFixed(2)}</span>
					</div>
				)}
				{!!vat && (
					<div>
						<span>VAT</span>
						<span>Rs. {vat.toFixed(2)}</span>
					</div>
				)}
				{!!esf && (
					<div>
						<span>Education Charge</span>
						<span>Rs. {esf.toFixed(2)}</span>
					</div>
				)}
				<div className="bill-summary-card__grand-total">
					<span>Total</span>
					<span>Rs. {totalSales?.toFixed(2)}</span>
				</div>
			</div>

			<p className="bill-summary-card__note">
				Showing a summary — click Print for the official document.
			</p>
		</div>
	);
}
